import { Auth } from "@genesislcap/foundation-comms";
import { cancelBondBid, getRowDataAfterSingleSubmit } from "../utils/bond-bid-utils";
import { deleteIcon, pencilIcon, clockRewindIcon } from "./icons";

export class EditDeleteCellRenderer {
  @Auth private auth: Auth;
  eGui: HTMLDivElement;
  editBtn: HTMLButtonElement;

  init(params) {
    this.eGui = document.createElement('div');
    this.eGui.style.display = 'flex';
    this.eGui.style.justifyContent = 'center';
    this.eGui.style.alignItems = 'center';
    this.eGui.style.gap = '4px';

    const isFirmOrPastDue =
      params.data?.STATUS === 'FIRM_IN' ||
      (params.data?.DUE_TIME && new Date(params.data.DUE_TIME).getTime() < Date.now());

    // Edit button
    const editBtn = document.createElement('button');
    this.editBtn = editBtn;
    editBtn.title = 'Edit';
    editBtn.style.background = 'none';
    editBtn.style.border = 'none';
    editBtn.style.cursor = 'pointer';
    editBtn.style.height = '29px';
    editBtn.style.color = 'var(--focus-stroke-outer)'; // Default color
    editBtn.innerHTML = pencilIcon;
    editBtn.disabled =
      !this.auth.currentUser?.hasPermission('SubmitBid') || !!isFirmOrPastDue;
    if (!editBtn.disabled) {
      editBtn.onmouseenter = () => {
        editBtn.style.color = 'var(--neutral-foreground-rest)'; // White on hover
      };
      editBtn.onmouseleave = () => {
        editBtn.style.color = 'var(--focus-stroke-outer)'; // Back to default color
      };
      editBtn.onclick = () => {
        params.node.setData({ ...params.node.data, isEditing: true });
        params.api.refreshCells({ rowNodes: [params.node], force: true });
        setTimeout(() => {
          params.api.setFocusedCell(params.node.rowIndex, 'BID_PRICE');
          params.api.startEditingCell({
            rowIndex: params.node.rowIndex,
            colKey: 'BID_PRICE',
          });
        }, 0);
      };
    }

    // Delete button
    const deleteBtn = document.createElement('button');
    deleteBtn.title = 'Delete';
    deleteBtn.style.background = 'none';
    deleteBtn.style.border = 'none';
    deleteBtn.style.cursor = 'pointer';
    deleteBtn.style.height = '29px';
    deleteBtn.style.color = 'var(--focus-stroke-outer)'; // Default color
    deleteBtn.innerHTML = deleteIcon;
    deleteBtn.disabled =
      !this.auth.currentUser?.hasPermission('CancelBid') || !!isFirmOrPastDue;
    if (!deleteBtn.disabled) {
      deleteBtn.onmouseenter = () => {
        deleteBtn.style.color = 'var(--neutral-foreground-rest)'; // White on hover
      };
      deleteBtn.onmouseleave = () => {
        deleteBtn.style.color = 'var(--focus-stroke-outer)'; // Back to default color
      };
      deleteBtn.onclick = async () => {
        if (params.data && params.data.BOND_ID) {
          const success = await cancelBondBid(params.data.BID_ID);
        }
      };
    }
    
    this.eGui.appendChild(editBtn);
    this.eGui.appendChild(deleteBtn);
  }

  getGui() {
    return this.eGui;
  }

  refresh(params) {
    return true;
  }

  focus() {
    if (this.editBtn) {
      this.editBtn.focus();
    }
  }
}

export class SubmitButtonCellRenderer {
  @Auth private auth: Auth;
  eGui: HTMLDivElement;
  submitBtn: HTMLButtonElement;

  init(params) {
    this.eGui = document.createElement('div');
    this.eGui.style.display = 'flex';
    this.eGui.style.justifyContent = 'center';
    this.eGui.style.alignItems = 'center';
    this.eGui.style.height = '100%';
    // Make the container focusable so keyboard navigation can land here
    this.eGui.tabIndex = 0;

    // Submit button
    const submitBtn = document.createElement('button');
    this.submitBtn = submitBtn;
    submitBtn.type = 'button';
    submitBtn.tabIndex = 0;
    submitBtn.innerText = params.colDef && params.colDef.headerName ? params.colDef.headerName : 'Submit';
    // Base styles (match #btn-submit)
    submitBtn.style.width = '76px';
    submitBtn.style.height = '24px';
    submitBtn.style.padding = '0px 12px 0px 12px';
    submitBtn.style.borderRadius = '4px';
    submitBtn.style.backgroundColor = '#2054bc';
    submitBtn.style.boxSizing = 'border-box';
    submitBtn.style.fontFamily = '"Roboto-Regular", "Roboto", sans-serif';
    submitBtn.style.color = '#ffffff';
    submitBtn.style.textAlign = 'center';
    submitBtn.style.lineHeight = 'normal';
    submitBtn.style.border = 'none';
    submitBtn.style.cursor = 'pointer';

    // Improved logic for disabling the button
    let isDisabled = false;
    if (params.colDef && params.colDef.cellRendererParams && typeof params.colDef.cellRendererParams.isDisabled === 'function') {
      const disabledResult = params.colDef.cellRendererParams.isDisabled(params.data) || !this.auth.currentUser?.hasPermission('SubmitBid');
      isDisabled = disabledResult;
    }
    submitBtn.disabled = !!isDisabled;
    if (isDisabled) {
      submitBtn.style.backgroundColor = 'rgba(255, 255, 255, 0)';
      submitBtn.style.color = 'rgba(241, 241, 241, 0.3)';
      submitBtn.style.cursor = 'not-allowed';
    }

    // Hover effect
    submitBtn.onmouseenter = () => {
      if (!submitBtn.disabled) {
        submitBtn.style.backgroundColor = '#336cdd';
        submitBtn.style.border = '1px solid';
      }
    };
    submitBtn.onmouseleave = () => {
      if (!submitBtn.disabled) {
        submitBtn.style.backgroundColor = '#2054bc';
        submitBtn.style.border = 'none';
      }
    };

    submitBtn.onclick = async (e) => {
      e.stopPropagation();
      if (!submitBtn.disabled && params.colDef && params.colDef.cellRendererParams && typeof params.colDef.cellRendererParams.actionClick === 'function') {
        
        const result = await params.colDef.cellRendererParams.actionClick(params.data);
        params.node.setData(getRowDataAfterSingleSubmit(params.node.data, result ?? { success: false, error: '' }));
        if (params.api?.redrawRows) {
          params.api.redrawRows({ rowNodes: [params.node] });
        }
      }
    };

    // Ensure keyboard Enter activates the button
    submitBtn.addEventListener('keydown', (ke: KeyboardEvent) => {
      if (ke.key === 'Enter' || ke.key === ' ') {
        ke.preventDefault();
        ke.stopPropagation();
        submitBtn.click();
      }
    });

    // When the cell container itself receives focus (e.g., tabbing back), move focus to the button
    this.eGui.addEventListener('focus', () => {
      if (!submitBtn.disabled) {
        submitBtn.focus();
      }
    });
    // Also handle Enter/Space on the container to trigger the action
    this.eGui.addEventListener('keydown', (ke: KeyboardEvent) => {
      if (ke.key === 'Enter' || ke.key === ' ') {
        ke.preventDefault();
        ke.stopPropagation();
        if (!submitBtn.disabled) {
          submitBtn.click();
        }
      }
    });

    this.eGui.appendChild(submitBtn);
  }

  getGui() {
    return this.eGui;
  }

  refresh(params) {
    return true;
  }

  focus() {
    if (this.submitBtn) {
      this.submitBtn.focus();
    }
  }
}

export class FocusableCellRenderer {
  eGui: HTMLDivElement;
  init(params) {
    this.eGui = document.createElement('div');
    this.eGui.style.width = '100%';
    this.eGui.style.height = '100%';
    this.eGui.style.display = 'flex';
    this.eGui.style.alignItems = 'center';
    this.eGui.style.paddingLeft = '8px';
    this.eGui.textContent = params.value || '';
    this.eGui.tabIndex = 0;
    this.eGui.style.outline = 'none';
    
    // Apply cell styling for cancelled bids
    if (params.data && params.data.BID_STATUS?.toUpperCase() === 'CANCELLED') {
      this.eGui.style.color = '#888888';
    }
  }
  
  refresh(params) {
    // Update text content
    this.eGui.textContent = params.value != null ? params.value : '';
    
    // Apply cell styling for cancelled bids
    if (params.data && params.data.BID_STATUS?.toUpperCase() === 'CANCELLED') {
      this.eGui.style.color = '#888888';
    } else {
      this.eGui.style.color = ''; // Reset to default color
    }
    
    return true;
  }
  
  getGui() { return this.eGui; }
  focus() { this.eGui.focus(); }
}

export class BidPriceCellEditor {
  eGui: HTMLInputElement;
  value: string;
  priceType: string;
  params: any;

  init(params) {
    this.params = params;
    this.eGui = document.createElement('input');
    this.eGui.type = 'number';
    this.eGui.style.width = '89%';
    this.eGui.style.height = '85%';
    this.eGui.style.outline = 'none';
    this.eGui.style.paddingLeft = '8px';
    this.eGui.style.fontSize = 'inherit';
    this.eGui.style.fontFamily = 'inherit';
    this.eGui.style.color = 'inherit';
    this.eGui.style.border = '1px solid var(--ag-foreground-color)';

    // Set the price type from the row data
    this.priceType = params.data.BID_PRICE_TYPE;
    
    // Set step and min/max based on price type
    this.setInputConstraints();

    // Set initial value
    this.value = params.value || '';
    this.eGui.value = this.value;

    // Add event listeners
    this.eGui.addEventListener('input', this.onInput.bind(this));
    this.eGui.addEventListener('keydown', this.onKeyDown.bind(this));
    this.eGui.addEventListener('paste', this.onPaste.bind(this));
    this.eGui.addEventListener('blur', this.onBlur.bind(this));

    // Focus the input
    setTimeout(() => {
      this.eGui.focus();
      this.eGui.select();
    }, 0);
  }

  setInputConstraints() {
    switch (this.priceType) {
      case 'Spread':
        // Spread: integer only (no decimals)
        this.eGui.step = '1';
        // Allow zero and negatives for Spread
        this.eGui.removeAttribute('min');
        break;
      case 'Price':
        // Price: up to 3 decimal places
        this.eGui.step = '0.001';
        // Disallow zero and negatives for Price by setting a minimal positive value
        this.eGui.min = '0.001';
        break;
      case 'Yield':
        // Yield: up to 4 decimal places
        this.eGui.step = '0.0001';
        // Allow zero and negatives for Yield
        this.eGui.removeAttribute('min');
        break;
      default:
        // Default: allow any number
        this.eGui.step = 'any';
        this.eGui.removeAttribute('min');
    }
  }

  onInput(event) {
    const input = event.target;
    let value = input.value;

    // Enforce decimal place restrictions based on price type
    switch (this.priceType) {
      case 'Spread':
        // Remove any decimal places for Spread
        value = value.replace(/\.\d*$/, '');
        break;
      case 'Price':
        // Limit to 3 decimal places for Price
        const priceMatch = value.match(/^\d*\.?\d{0,3}/);
        value = priceMatch ? priceMatch[0] : value;
        break;
      case 'Yield':
        // Limit to 4 decimal places for Yield (allow negative)
        const yieldMatch = value.match(/^-?\d*\.?\d{0,4}/);
        value = yieldMatch ? yieldMatch[0] : value;
        break;
    }

    // Update input value if it was modified
    if (value !== input.value) {
      input.value = value;
    }

    this.value = value;
  }

  onKeyDown(event) {
    // Custom Tab handling: commit edit and focus the Submit button cell
    if (event.key === 'Tab') {
      event.preventDefault();
      event.stopPropagation();
      // Commit current edit
      if (this.params && this.params.api) {
        const rowIndex = this.params.node.rowIndex;
        this.params.api.stopEditing();
        // Focus SUBMIT_BID cell so Enter immediately triggers the button
        // Use a slight delay to let onCellValueChanged/redraw complete
        window.setTimeout(() => {
          this.params.api.setFocusedCell(rowIndex, 'SUBMIT_BID');
          // Then focus the renderer's inner button to ensure Enter works
          window.setTimeout(() => {
            const instances = this.params.api.getCellRendererInstances?.({ rowNodes: [this.params.node], columns: ['SUBMIT_BID'] }) || [];
            instances.forEach((inst: any) => {
              if (typeof inst.focus === 'function') {
                inst.focus();
              }
            });
          }, 0);
        }, 30);
      }
      return;
    }

    // Shift+Tab: move focus back to price type
    if (event.key === 'Tab' && event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      if (this.params && this.params.api) {
        const rowIndex = this.params.node.rowIndex;
        this.params.api.stopEditing();
        window.setTimeout(() => {
          this.params.api.setFocusedCell(rowIndex, 'BID_PRICE_TYPE');
        }, 30);
      }
      return;
    }

    // Allow navigation keys
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', 'Escape'].includes(event.key)) {
      return;
    }

    // Allow backspace and delete
    if (['Backspace', 'Delete'].includes(event.key)) {
      return;
    }

    // Allow paste shortcuts (Ctrl+V, Cmd+V)
    if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
      return;
    }

    // Allow numbers, decimal point and minus sign
    if (/[^\d.\-]/.test(event.key) === false) {
      // For Spread, don't allow decimal point
      if (this.priceType === 'Spread' && event.key === '.') {
        event.preventDefault();
        return;
      }

      // For Price and Yield, only allow one decimal point
      if (event.key === '.' && this.eGui.value.includes('.')) {
        event.preventDefault();
        return;
      }

      // For Price, disallow negative sign
      if (this.priceType === 'Price' && (event.key === '-' || event.key === 'Subtract' || event.key === 'Minus')) {
        event.preventDefault();
        return;
      }

      // For Yield/Spread, allow '-' only at start and only once (support numpad minus)
      if ((this.priceType === 'Yield' || this.priceType === 'Spread') && (event.key === '-' || event.key === 'Subtract' || event.key === 'Minus')) {
        const val = this.eGui.value || '';
        const selectionStart = (this.eGui.selectionStart as number | null) ?? 0;
        const selectionEnd = (this.eGui.selectionEnd as number | null) ?? 0;
        const fullSelected = selectionStart === 0 && selectionEnd === val.length;
        // If already contains '-' and not fully selected, block adding another minus
        if (val.includes('-') && !fullSelected) {
          event.preventDefault();
          return;
        }
        // If not at the beginning and not fully selected, block
        if (selectionStart !== 0 && !fullSelected) {
          event.preventDefault();
          return;
        }
        // Otherwise allow
        return;
      }

      return;
    }

    // Prevent other keys
    event.preventDefault();
  }

  onPaste(event) {
    if (this.priceType === 'None' || this.priceType === undefined) {
      event.preventDefault();
      return;
    }
  }

  onBlur() {
    // Format the value based on price type for display
    if (this.value) {
      const numValue = parseFloat(this.value);
      if (!isNaN(numValue)) {
        switch (this.priceType) {
          case 'Spread':
            // Spread: integer only
            this.value = Math.round(numValue).toString();
            break;
          case 'Price':
            // Price: always show 3 decimal places; disallow <= 0
            if (numValue <= 0) {
              this.value = '';
              this.eGui.value = '';
              return;
            }
            this.value = numValue.toFixed(3);
            break;
          case 'Yield':
            // Yield: round to 3 decimal places for display (can be negative or zero)
            this.value = numValue.toFixed(3);
            break;
        }
      }
    }
  }

  getGui() {
    return this.eGui;
  }

  getValue() {
    // Enforce final validation on return to handle cases where blur might not run
    if (this.priceType === 'Price') {
      const num = parseFloat(this.value);
      if (!isFinite(num) || num <= 0) {
        return '';
      }
      // Normalize to 3dp for Price on commit
      return num.toFixed(3);
    }
    if (this.priceType === 'Spread') {
      const num = parseFloat(this.value);
      if (isFinite(num)) {
        return Math.round(num).toString();
      }
      return this.value;
    }
    if (this.priceType === 'Yield') {
      const num = parseFloat(this.value);
      if (isFinite(num)) {
        // normalize to 3dp on commit for consistency
        return num.toFixed(3);
      }
      return this.value;
    }
    return this.value;
  }

  isPopup() {
    return false;
  }

  focus() {
    this.eGui.focus();
  }
}

export class AuditButtonCellRenderer {
  eGui: HTMLDivElement;
  auditBtn: HTMLButtonElement;

  init(params) {
    this.eGui = document.createElement('div');
    this.eGui.style.display = 'flex';
    this.eGui.style.justifyContent = 'center';
    this.eGui.style.alignItems = 'center';
    this.eGui.style.height = '100%';

    // Audit button
    const auditBtn = document.createElement('button');
    this.auditBtn = auditBtn;
    auditBtn.type = 'button';
    auditBtn.tabIndex = 0;
    auditBtn.title = 'View Audit History';
    auditBtn.style.background = 'none';
    auditBtn.style.border = 'none';
    auditBtn.style.cursor = 'pointer';
    auditBtn.style.height = '29px';
    auditBtn.style.width = '29px';
    auditBtn.style.display = 'flex';
    auditBtn.style.alignItems = 'center';
    auditBtn.style.justifyContent = 'center';
          auditBtn.style.color = 'var(--focus-stroke-outer)'; // Default color
      auditBtn.innerHTML = clockRewindIcon;
      
      // Add hover effects
      auditBtn.onmouseenter = () => {
        auditBtn.style.color = 'var(--neutral-foreground-rest)'; // White on hover
      };
      auditBtn.onmouseleave = () => {
        auditBtn.style.color = 'var(--focus-stroke-outer)'; // Back to default color
      };
    
    auditBtn.onclick = () => {
      if (params.data && params.data.BOND_ID) {
        // Emit a custom event to open the tranche flyout with audit tab
        const event = new CustomEvent('openAuditFlyout', {
          detail: {
            bondData: params.data,
            openWithAuditTab: true
          },
          bubbles: true,
          composed: true
        });
        
        // Dispatch the event from the button element - it will bubble up naturally
        auditBtn.dispatchEvent(event);
      }
    };

    this.eGui.appendChild(auditBtn);
  }

  getGui() {
    return this.eGui;
  }

  refresh(params) {
    return true;
  }

  focus() {
    if (this.auditBtn) {
      this.auditBtn.focus();
    }
  }
}